import React from 'react';
import { AlertTriangle, Lock, Crown } from 'lucide-react';
import { useLocalization } from '../../contexts/LocalizationContext';
import { usePlanLimitations } from '../../hooks/usePlanLimitations';

interface PlanLimitationWarningProps {
  itemType: 'office' | 'client' | 'file' | 'team_member';
  onUpgrade?: () => void;
  className?: string;
}

const PlanLimitationWarning: React.FC<PlanLimitationWarningProps> = ({
  itemType,
  onUpgrade,
  className = ''
}) => {
  const { language, isRTL } = useLocalization();
  const { limitations, getLimitationMessage, shouldShowUpgradePrompt, getSuggestedUpgradePlan } = usePlanLimitations();

  if (!limitations) return null;

  const limitation = limitations[itemType === 'team_member' ? 'teamMembers' : `${itemType}s` as keyof typeof limitations] as any;

  if (!limitation || !limitation.isExceeded) return null;

  const strings = {
    en: {
      title: 'Plan Limit Exceeded',
      message: getLimitationMessage(itemType),
      upgradeButton: 'Upgrade Plan',
      upgradeDescription: 'Upgrade to access more features and remove limitations'
    },
    ar: {
      title: 'تم تجاوز حد الخطة',
      message: getLimitationMessage(itemType),
      upgradeButton: 'ترقية الخطة',
      upgradeDescription: 'قم بالترقية للوصول إلى المزيد من الميزات وإزالة القيود'
    }
  };

  const t = strings[language];

  return (
    <div className={`bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-700 rounded-lg p-4 ${className}`}>
      <div className="flex items-start space-x-3 rtl:space-x-reverse">
        <div className="flex-shrink-0">
          <div className="p-2 bg-orange-100 dark:bg-orange-900 rounded-full">
            <AlertTriangle className="w-5 h-5 text-orange-600 dark:text-orange-400" />
          </div>
        </div>
        
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-medium text-orange-800 dark:text-orange-200">
            {t.title}
          </h4>
          <p className="mt-1 text-sm text-orange-700 dark:text-orange-300">
            {t.message}
          </p>
          
          {shouldShowUpgradePrompt() && onUpgrade && (
            <div className="mt-3">
              <button
                onClick={onUpgrade}
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 transition-colors"
              >
                <Crown className="w-4 h-4 mr-2 rtl:mr-0 rtl:ml-2" />
                {t.upgradeButton}
              </button>
              <p className="mt-2 text-xs text-orange-600 dark:text-orange-400">
                {t.upgradeDescription}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PlanLimitationWarning;
